/**
 * Низкоуровневые утилиты для работы с SEO-метаданными в <head>
 */

import { DEFAULT_PAGE_TITLE } from '@/constants'

const BRAND_SUFFIX = 'КРОН'
const MANAGED_META_IDS = {
  description: 'seo-meta-description',
  robots: 'seo-meta-robots',
  canonical: 'seo-link-canonical',
  ogTitle: 'seo-og-title',
  ogDescription: 'seo-og-description',
  ogImage: 'seo-og-image',
  ogUrl: 'seo-og-url',
  ogType: 'seo-og-type',
  ogLocale: 'seo-og-locale',
  ogSiteName: 'seo-og-site-name',
  twitterCard: 'seo-twitter-card',
  twitterTitle: 'seo-twitter-title',
  twitterDescription: 'seo-twitter-description',
  twitterImage: 'seo-twitter-image'
}

export function setDefaultPageTitle() {
  document.title = DEFAULT_PAGE_TITLE
}

/**
 * @param {string} pageTitle
 */
export function setPageTitle(pageTitle) {
  const normalized = (pageTitle || '').trim()

  if (!normalized) {
    setDefaultPageTitle()
    return
  }

  if (normalized.includes(BRAND_SUFFIX)) {
    document.title = normalized
    return
  }

  document.title = `${normalized} — ${BRAND_SUFFIX}`
}

export function setMetaDescription(description) {
  upsertMetaByName('description', description, MANAGED_META_IDS.description)
}

export function setRobots(content) {
  if (!content) {
    removeManagedElement('meta', MANAGED_META_IDS.robots)
    return
  }

  upsertMetaByName('robots', content, MANAGED_META_IDS.robots)
}

export function setCanonical(url) {
  if (!url) {
    removeManagedElement('link', MANAGED_META_IDS.canonical)
    return
  }

  upsertLinkRel('canonical', url, MANAGED_META_IDS.canonical)
}

export function setOpenGraph({ title, description, image, url, type = 'website', siteName = 'КРОН' }) {
  upsertMetaByProperty('og:title', title, MANAGED_META_IDS.ogTitle)
  upsertMetaByProperty('og:description', description, MANAGED_META_IDS.ogDescription)
  upsertMetaByProperty('og:type', type, MANAGED_META_IDS.ogType)
  upsertMetaByProperty('og:locale', 'ru_RU', MANAGED_META_IDS.ogLocale)
  upsertMetaByProperty('og:site_name', siteName, MANAGED_META_IDS.ogSiteName)

  if (url) {
    upsertMetaByProperty('og:url', url, MANAGED_META_IDS.ogUrl)
  } else {
    removeManagedElement('meta', MANAGED_META_IDS.ogUrl)
  }

  if (image) {
    upsertMetaByProperty('og:image', image, MANAGED_META_IDS.ogImage)
  } else {
    removeManagedElement('meta', MANAGED_META_IDS.ogImage)
  }
}

export function setTwitterCard({ title, description, image, card = 'summary_large_image' }) {
  upsertMetaByName('twitter:card', card, MANAGED_META_IDS.twitterCard)
  upsertMetaByName('twitter:title', title, MANAGED_META_IDS.twitterTitle)
  upsertMetaByName('twitter:description', description, MANAGED_META_IDS.twitterDescription)

  if (image) {
    upsertMetaByName('twitter:image', image, MANAGED_META_IDS.twitterImage)
  } else {
    removeManagedElement('meta', MANAGED_META_IDS.twitterImage)
  }
}

const JSON_LD_SELECTOR = 'script[type="application/ld+json"][data-seo-jsonld]'

export function setJsonLd(schemas) {
  clearJsonLd()

  const list = Array.isArray(schemas) ? schemas.filter(Boolean) : [schemas].filter(Boolean)
  if (!list.length) return

  list.forEach((schema, index) => {
    const script = document.createElement('script')
    script.type = 'application/ld+json'
    script.setAttribute('data-seo-jsonld', String(index))
    script.textContent = JSON.stringify(schema)
    document.head.appendChild(script)
  })
}

export function clearJsonLd() {
  document.querySelectorAll(JSON_LD_SELECTOR).forEach((node) => node.remove())
}

export function truncateDescription(description, maxLength = 160) {
  if (!description) return ''
  return description.length > maxLength
    ? `${description.substring(0, maxLength - 1).trim()}…`
    : description
}

function upsertMetaByName(name, content, id) {
  if (!content) return

  let element = document.getElementById(id)
  if (!element) {
    element = document.createElement('meta')
    element.id = id
    element.setAttribute('name', name)
    document.head.appendChild(element)
  }

  element.setAttribute('content', content)
}

function upsertMetaByProperty(property, content, id) {
  if (!content) return

  let element = document.getElementById(id)
  if (!element) {
    element = document.createElement('meta')
    element.id = id
    element.setAttribute('property', property)
    document.head.appendChild(element)
  }

  element.setAttribute('content', content)
}

function upsertLinkRel(rel, href, id) {
  let element = document.getElementById(id)
  if (!element) {
    element = document.createElement('link')
    element.id = id
    element.setAttribute('rel', rel)
    document.head.appendChild(element)
  }

  element.setAttribute('href', href)
}

function removeManagedElement(tagName, id) {
  const element = document.getElementById(id)
  if (element) element.remove()
}
